import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { showNotification } from '../../LanghoutBetonPortal';
import {
  formatDate,
  convertRetourOrderStatusToReadable,
  arraysEqual,
  RetourOrderStatusApproved,
  convertReadableToRetourOrderStatus,
  RetourOrderStatusReadyToCheck,
  RetourOrderStatusProcessed,
} from '../../util/util';
import { repeat } from 'lit/directives/repeat.js';
import { Product, RetourOrder } from '../../types/types';
import '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';
import '../../elements/langhout-button/langhout-button';
import '../../elements/list-nagivation/list-navigation';
import '../../elements/langhout-checkbox/langhout-checkbox';
import { LanghoutCheckbox } from '../../elements/langhout-checkbox/langhout-checkbox';
import { DropdownMultiselectElement } from '../../elements/dropdown-multiselect-element/dropdown-multiselect-element';

import { messageIcon, refreshIcon, sortIcon } from '../../elements/icons/icons';
import { getQueryParamURL, getSearchParam } from '../../util/list_util';
import { sharedStyles } from '../../elements/shared-styles';

@customElement('langhout-beton-list-retour-orders')
export class LanghoutBetonListRetourOrders extends LitElement {
  static styles = [
    sharedStyles,
    css`
      .row {
        display: flex;
        flex-direction: row;
      }

      .row-flex {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        flex-flow: row wrap;
      }

      .filter-row {
        min-height: 130px;
      }

      .filter-column {
        margin-bottom: 20px;
      }

      .fat {
        font-weight: 900;
        color: var(--menu-color);
      }

      .label {
        font-weight: bold;
      }

      @media (max-width: 701px) {
        .label {
          text-align: left;
        }
      }

      .product-column {
        text-align: left;
        width: 100%;
        min-width: 120px;
        min-height: calc(54px - 24px);
      }

      .order-id-click {
        font-weight: bold;
        text-decoration: underline;
        text-decoration-color: var(--primary-color);
      }

      .order-id-click:hover {
        cursor: pointer;
      }

      .border-bot {
        border-bottom: 2px solid var(--menu-color);
      }

      .column-large {
        text-align: left;
        min-width: calc(350px - 20px);
        max-width: calc(350px - 20px);
      }

      .table-wrapper {
        overflow-x: auto;
      }

      .table-header {
        color: var(--text-color-light);
      }

      .header-column {
        background-color: var(--menu-color);
        padding-left: 12px;
      }

      .column-border-right {
        border-right: 2px solid var(--menu-color);
      }

      .navigation-wrapper {
        margin-top: 20px;
      }

      .checkbox-column {
        width: 23px;
        min-width: 23px;
      }

      .checkbox-wrapper {
        padding-left: 12px;
      }

      .checkbox-wrapper:hover {
        cursor: auto;
      }

      langhout-button {
        margin-left: 12px;
      }

      :host([disableButtons]) {
        pointer-events: none;
        opacity: 0.7;
      }

      .sort-icon-wrapper {
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .sort-icon {
        transform: rotate(90deg);
      }

      .sort-icon:hover {
        cursor: pointer;
      }

      .sort-active {
        color: var(--text-color-main);
      }

      .order-id-column {
        padding-left: 10px;
        padding-right: 10px;
      }
      .message-icon-wrapper {
        padding-left: 10px;
      }

      #resetButton {
        margin-top: 20px;
      }

      .green {
        color: var(--primary-color);
      }
    `,
  ];

  private requestCounter = 0;

  private listTimer = 0;

  @property({ type: String })
  private lastPath = 'unknown';

  @property({ type: Array })
  private retourOrders: RetourOrder[] = [];

  @property({ type: Number })
  private retourOrderCount = 0;

  @query('#fromDate')
  public fromDate?: HTMLInputElement;

  @query('#dropdownElement')
  public dropdownElement?: DropdownMultiselectElement;

  @query('#untilDate')
  public untilDate?: HTMLInputElement;

  @query('#orderID')
  public orderID?: HTMLInputElement;

  @query('#shippingID')
  public shippingID?: HTMLInputElement;

  @property({ type: Array, attribute: true })
  public selected: Array<string> = [];

  private offset = 0;

  private limit = 50;

  @property({ type: Boolean, reflect: true })
  private disableButtons = false;

  @property({ type: Array, reflect: true, attribute: true })
  private checkBoxes: boolean[] = [];

  private lastIDClicked = '';

  private orderByColumn = '';

  private orderByDirection = '';

  private shiftDown = false;

  private allCheckbox = false;

  constructor() {
    super();
    document.addEventListener('keydown', this.keyDownListener);
    document.addEventListener('keyup', this.keyUpListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    document.removeEventListener('keydown', this.keyDownListener);
    document.removeEventListener('keydown', this.keyUpListener);
  }

  private keyDownListener = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this.shiftDown = true;
    }
  };

  private keyUpListener = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this.shiftDown = false;
    }
  };

  protected routerLocationChanged = (): void => {
    this.setPropertiesFromURL(false);
    this.listWithBackoff();
  };

  protected setPropertiesFromURL = (firstUpdate = false): void => {
    const url = getQueryParamURL();
    if (this.orderID) {
      this.orderID.value = getSearchParam(url, 'order_id');
    }
    if (this.shippingID) {
      this.shippingID.value = getSearchParam(url, 'shipping_id');
    }

    if (this.dropdownElement) {
      const stockStatuses = getSearchParam(url, 'statusses');
      if (stockStatuses !== '') {
        const res: string[] = [];
        const statuses = stockStatuses.split(',');
        statuses.forEach(e => {
          const status = convertRetourOrderStatusToReadable(e);
          if (!res.includes(status)) {
            res.push(status);
          }
        });
        if (firstUpdate) {
          this.selected = res;
        }
        this.dropdownElement.selected = res;
      } else {
        if (firstUpdate) {
          this.selected = [
            'Aangemaakt',
            'Controle',
            'Wordt gecontroleerd',
            'Goedgekeurd',
          ];
        }
        this.dropdownElement.selected = [
          'Aangemaakt',
          'Controle',
          'Wordt gecontroleerd',
          'Goedgekeurd',
        ];
      }
    }
    if (this.fromDate) {
      const splittedDate = getSearchParam(url, 'from').split('-');
      if (splittedDate.length === 3) {
        this.fromDate.value = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
      } else {
        this.fromDate.value = '';
      }
    }

    if (this.untilDate) {
      const splittedDate = getSearchParam(url, 'until').split('-');
      if (splittedDate.length === 3) {
        this.untilDate.value = `${splittedDate[2]}-${splittedDate[1]}-${splittedDate[0]}`;
      } else {
        this.untilDate.value = '';
      }
    }

    this.orderByColumn = getSearchParam(url, 'order_by');
    this.orderByDirection = getSearchParam(url, 'order_direction');
  };

  protected firstUpdated(): void {
    if (this.dropdownElement) {
      this.dropdownElement.selectable = [
        'Aangemaakt',
        'Controle',
        'Wordt gecontroleerd',
        'Goedgekeurd',
        'Verwerkt',
      ];
    }
    window.addEventListener(
      'vaadin-router-location-changed',
      this.routerLocationChanged as EventListener,
    );
    this.setPropertiesFromURL(true);
    this.listRetourOrders();
  }

  render() {
    return html`
      ${this.renderFilterRow()}
      <div class="table-wrapper">
        <div class="row table-header">
          <div class="column header-column checkbox-column sort-icon-wrapper">
            <langhout-checkbox
              .checked=${this.allCheckbox}
              reverse
              @click="${() => {
                if (!this.shadowRoot) {
                  return;
                }
                this.allCheckbox = !this.allCheckbox;
                for (let i = 0; i < this.checkBoxes.length; i++) {
                  this.checkBoxes[i] = this.allCheckbox;
                }
                const temp = this.checkBoxes;
                this.checkBoxes = [];
                this.checkBoxes = temp;
              }}"
            ></langhout-checkbox>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Order ID</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('order_id')}"
                @click="${() => {
                  this.orderColumns('order_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Klant</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('customer_name')}"
                @click="${() => {
                  this.orderColumns('customer_name');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Vracht ID</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('shipping_id')}"
                @click="${() => {
                  this.orderColumns('shipping_id');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Totaal gewicht</h3>
              <div class="fill"></div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Retourdatum</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('retour_date')}"
                @click="${() => {
                  this.orderColumns('retour_date');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
          <div class="column header-column product-column">
            <div class="row">
              <h3>Status</h3>
              <div class="fill"></div>
              <div
                class="sort-icon-wrapper ${this.isSortActive('status')}"
                @click="${() => {
                  this.orderColumns('status');
                }}"
              >
                ${sortIcon()}
              </div>
            </div>
          </div>
        </div>
        ${this.renderRows()}
      </div>
      <div class="navigation-wrapper">
        <list-navigation
          index="${this.offset}"
          limit="${this.limit}"
          total="${this.retourOrderCount}"
          @change="${(e: CustomEvent) => {
            if (this.offset != e.detail.value) {
              this.offset = parseInt(e.detail.value);
              this.listWithBackoffWithoutReset();
            }
          }}"
        ></list-navigation>
      </div>
    `;
  }

  renderFilterRow = () => {
    return html`
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Order ID</div>
          <div class="input-field">
            <input
              type="text"
              id="orderID"
              name="orderID"
              @input="${() => {
                this.listRetourOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Vracht ID</div>
          <div class="input-field">
            <input
              type="text"
              id="shippingID"
              name="shippingID"
              @input="${() => {
                this.listRetourOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Status</div>
          <div class="input-field">
            <dropdown-multiselect-element
              id="dropdownElement"
              @change="${(e: CustomEvent) => {
                const newSelected = <Array<string>>JSON.parse(e.detail.value);
                if (!arraysEqual(newSelected, this.selected)) {
                  this.selected = newSelected;
                  this.listRetourOrders();
                }
              }}"
            ></dropdown-multiselect-element>
          </div>
        </div>

        <div class="fill"></div>
        <div class="row filter-column">
          <langhout-button
            text="Controleren"
            @click="${async () => {
              this.disableButtons = true;
              await this.selectionReadyToBeProcessed();
              this.disableButtons = false;
            }}"
          ></langhout-button>
          <langhout-button
            text="Retour invoeren"
            @click="${(): void => {
              window.location.href = '/create-retour-order';
            }}"
          ></langhout-button>
        </div>
      </div>
      <div class="row-flex filter-row">
        <div class="column filter-column">
          <div class="label">Van</div>
          <div class="input-field">
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              @change="${() => {
                this.listRetourOrders();
              }}"
            />
          </div>
        </div>
        <div class="column filter-column">
          <div class="label">Tot</div>
          <div class="input-field">
            <input
              type="date"
              id="untilDate"
              name="untilDate"
              @change="${() => {
                this.listRetourOrders();
              }}"
            />
          </div>
        </div>
        <langhout-button
          id="resetButton"
          text="Reset filters"
          @click="${(): void => {
            window.location.href = '/retour-orders';
            location.reload();
          }}"
        ></langhout-button>
        <div class="fill"></div>
        <div
          @click="${() => {
            showNotification('Refreshing Retour Orders');
            this.listWithBackoff();
          }}"
        >
          ${refreshIcon()}
        </div>
      </div>
    `;
  };

  renderRows = () => {
    return html` ${repeat(
      this.retourOrders,
      retourOrder => retourOrder.id,
      (retourOrder, index) => {
        let tableColumnClass = 'border-bot';
        if (index == this.retourOrders.length - 1) {
          tableColumnClass = '';
        }
        return html`
          <div
            class="row table-row"
            @click="${() => {
              window.location.href = `/view-retour-order?id=${retourOrder.id}`;
            }}"
          >
            <div
              class="checkbox-wrapper column-border-right ${tableColumnClass}"
              @click="${(e: Event) => {
                e.stopPropagation();
              }}"
            >
              <langhout-checkbox
                .checked="${this.checkBoxes[index]}"
                id="${retourOrder.id}"
                class="checkbox"
                @click="${(e: Event) => {
                  e.stopPropagation();
                  this.updateCheckBoxes(retourOrder);
                }}"
              ></langhout-checkbox>
            </div>
            <div
              class="row order-id-column table-column order-id-click column-border-right product-column ${tableColumnClass}"
            >
              ${retourOrder.order_id}
              <div class="fill"></div>
              <div>${this.renderMessageIcon(retourOrder)}</div>
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${retourOrder.customer_name}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${retourOrder.shipping_id}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${this.getWeightTotalText(retourOrder)}
            </div>
            <div
              class="column table-column column-border-right product-column ${tableColumnClass}"
            >
              ${formatDate(retourOrder.retour_date)}
            </div>
            <div class="column table-column product-column ${tableColumnClass}">
              ${this.renderStatus(retourOrder)}
            </div>
          </div>
        `;
      },
    )}`;
  };

  renderMessageIcon = (order: RetourOrder) => {
    if (order.comments.length === 0) {
      return html``;
    }
    return html` <div class="message-icon-wrapper">${messageIcon()}</div> `;
  };

  selectionReadyToBeProcessed = async () => {
    const idsToBeProcessed: string[] = this.getCheckboxIDs();
    if (idsToBeProcessed.length === 0) {
      showNotification('Selecteer tenminste 1 retour order om te controleren');
      return;
    }
    await this.updateRetourOrdersReadyToBeChecked(idsToBeProcessed);
    this.listWithBackoff();
    this.resetCheckBoxes();
  };

  getStatusHTML = (status: string) => {
    if (status === RetourOrderStatusApproved) {
      return html`
        <div class="fat">${convertRetourOrderStatusToReadable(status)}</div>
      `;
    }
    return html` ${convertRetourOrderStatusToReadable(status)} `;
  };

  getWeightTotalText = (retourOrder: RetourOrder): string => {
    let totalWeight = 0;

    retourOrder.products.forEach((p: Product) => {
      totalWeight += p.amount * p.weight;
    });
    totalWeight = Math.round(totalWeight);

    return `${totalWeight.toLocaleString('nl-NL')} kg`;
  };

  updateCheckBoxes = (retourOrder: RetourOrder) => {
    if (!this.shadowRoot) {
      return;
    }

    const clickedIndex = this.getIndexForID(retourOrder.id);
    this.checkBoxes[clickedIndex] = !this.checkBoxes[clickedIndex];
    const setToValue = this.checkBoxes[clickedIndex];
    if (this.lastIDClicked != '' && this.shiftDown) {
      const lastClickedIndex = this.getIndexForID(this.lastIDClicked);
      for (
        let i = Math.min(clickedIndex, lastClickedIndex);
        i < Math.max(clickedIndex, lastClickedIndex);
        i++
      ) {
        this.checkBoxes[i] = setToValue;
      }
    }
    const temp = this.checkBoxes;
    this.checkBoxes = [];
    this.checkBoxes = temp;
    this.lastIDClicked = retourOrder.id;
  };

  getIndexForID = (id: string) => {
    for (let i = 0; i < this.retourOrders.length; i++) {
      if (this.retourOrders[i].id === id) {
        return i;
      }
    }
    return 0;
  };

  isSortActive = (column: string): string => {
    if (this.orderByColumn === column) {
      return 'sort-active';
    }
    return '';
  };

  orderColumns = (column: string) => {
    if (this.orderByColumn === column && this.orderByDirection === '') {
      this.orderByDirection = 'asc';
    } else if (
      this.orderByColumn === column &&
      this.orderByDirection === 'asc'
    ) {
      this.orderByColumn = '';
      this.orderByDirection = '';
    } else {
      this.orderByColumn = column;
      this.orderByDirection = '';
    }
    this.listRetourOrders();
  };

  listWithBackoffWithoutReset = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.listRetourOrders();
    }, 50);
  };

  listWithBackoff = () => {
    clearInterval(this.listTimer);
    this.listTimer = window.setTimeout(() => {
      this.offset = 0;
      this.listRetourOrders();
    }, 50);
  };

  createFilterArray = (): string[] => {
    const filterArray: string[] = [];
    const statusFilterIntegerArray: Array<string> = [];
    this.selected.forEach(element => {
      statusFilterIntegerArray.push(
        convertReadableToRetourOrderStatus(element),
      );
    });
    if (statusFilterIntegerArray.length == 0) {
      return [];
    }
    filterArray.push(`statusses=${statusFilterIntegerArray.join(',')}`);

    const fromDate = this.fromDate ? this.fromDate.value : '';
    if (fromDate != '') {
      filterArray.push(`from=${formatDate(fromDate)}`);
    }

    const untilDate = this.untilDate ? this.untilDate.value : '';
    if (untilDate != '') {
      filterArray.push(`until=${formatDate(untilDate)}`);
    }

    const orderID = this.orderID ? this.orderID.value : '';
    if (orderID != '') {
      filterArray.push(`order_id=${orderID}`);
    }

    const shippingID = this.shippingID ? this.shippingID.value : '';
    if (shippingID != '') {
      filterArray.push(`shipping_id=${shippingID}`);
    }

    filterArray.push(`order_by=${this.orderByColumn}`);
    filterArray.push(`order_direction=${this.orderByDirection}`);
    return filterArray;
  };

  listRetourOrders = async () => {
    this.requestCounter++;
    const requestCounter = this.requestCounter;

    const filterArray = this.createFilterArray();
    if (filterArray.length === 0) {
      return;
    }
    let path = `/retour-orders?limit=${this.limit}&offset=${this.offset}`;
    const filter = filterArray.join('&');
    if (filter !== '') {
      path += '&' + filter;
    }
    const result = await authenticatedAPICall(path, 'GET', new Headers());
    if (!isSuccessfulAPIResponse(result)) {
      return;
    }
    if (requestCounter != this.requestCounter) {
      return;
    }
    const xCount = result.headers.get('x-count');
    if (xCount) {
      this.retourOrderCount = parseInt(xCount);
    }

    this.retourOrders = result.body;
    // If we have a selected view order overwrite.
    if (this.retourOrders.length != this.checkBoxes.length) {
      this.checkBoxes = [];
      result.body.forEach(() => {
        this.checkBoxes.push(false);
      });
      this.allCheckbox = false;
      this.lastIDClicked = '';
    }
  };

  resetCheckBoxes = () => {
    this.checkBoxes = [];
  };

  renderStatus = (retourOrder: RetourOrder) => {
    if (retourOrder.status === RetourOrderStatusProcessed) {
      return html` <div class="value green">
        ${convertRetourOrderStatusToReadable(retourOrder.status)}
      </div>`;
    }
    return html` <div class="value">
      ${convertRetourOrderStatusToReadable(retourOrder.status)}
    </div>`;
  };

  updateRetourOrdersReadyToBeChecked = async (orderIDs: string[]) => {
    for (let i = 0; i < orderIDs.length; i++) {
      const path = `/retour-orders/${orderIDs[i]}/status`;
      const result = await authenticatedAPICall(
        path,
        'PUT',
        new Headers(),
        JSON.stringify({
          status: RetourOrderStatusReadyToCheck,
        }),
      );
      if (isSuccessfulAPIResponse(result) && !result.body.success) {
        showNotification(
          'Niet alle orders konden aangepast worden naar controleren',
        );
      }
    }
  };

  getCheckboxIDs = (): string[] => {
    if (!this.shadowRoot) {
      return [];
    }
    const checkboxes = this.shadowRoot.querySelectorAll('.checkbox');
    const selectedIDs: string[] = [];
    checkboxes.forEach(checkbox => {
      if ((<LanghoutCheckbox>checkbox).checked) {
        selectedIDs.push(checkbox.id);
      }
    });
    return selectedIDs;
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'langhout-beton-list-retour-orders': LanghoutBetonListRetourOrders;
  }
}
